import type { ObjectType, SpotDetail } from "@divespots/contracts";
import { OBJECT_TYPES } from "@divespots/contracts";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { ObjectIcon } from "../components/ObjectIcon.tsx";
import { SegmentedControl } from "../components/SegmentedControl.tsx";
import type { FieldErrors, ObjectForm } from "./forms.ts";
import { LocationPicker } from "./LocationPicker.tsx";
import { Field, LocaleField, LocaleTabs, errorAt } from "./ui.tsx";
import type { Status } from "@divespots/contracts";

const STATUSES: Status[] = ["draft", "published", "pending"];

export interface ObjectFieldsProps {
  /** Staff also set the measured depth and the status; divers never do. */
  mode: "staff" | "diver";
  form: ObjectForm;
  errors: FieldErrors;
  set: <K extends keyof ObjectForm>(k: K, v: ObjectForm[K]) => void;
  setForm: Dispatch<SetStateAction<ObjectForm>>;
  setErrors: Dispatch<SetStateAction<FieldErrors>>;
  locale: "de" | "en" | "fr" | "it";
  setLocale: (l: "de" | "en" | "fr" | "it") => void;
  /** Entry point of the spot: the picker starts there and shows the distance to it. */
  spotLocation: { lat: number; lon: number };
  /** Staff object form: offers the embedded 3D lakebed of this spot in the position picker. */
  spot?: SpotDetail | undefined;
}

/** Type, name, description, location (and for staff: measured depth, status) of a dive object. */
export function ObjectFields({
  mode,
  form,
  errors,
  set,
  setForm,
  setErrors,
  locale,
  setLocale,
  spotLocation,
  spot,
}: ObjectFieldsProps) {
  const { t } = useTranslation();
  return (
    <>
      <Field label={t("admin.objects.type")}>
        {() => (
          <div className="adm-types" role="group" aria-label={t("admin.objects.type")}>
            {OBJECT_TYPES.map((ty: ObjectType) => (
              <button
                key={ty}
                type="button"
                aria-pressed={form.type === ty}
                className="adm-type"
                onClick={() => set("type", ty)}
              >
                <ObjectIcon type={ty} size={20} />
                <span className="ds-text-caption-1">{t(`objects.type.${ty}`)}</span>
              </button>
            ))}
          </div>
        )}
      </Field>

      <LocaleTabs
        label={t("admin.i18n.language")}
        value={locale}
        onChange={setLocale}
        fields={[form.name, form.description]}
      />
      <LocaleField
        label={t("admin.objects.name")}
        required
        value={form.name}
        locale={locale}
        error={errorAt(errors, "name")}
        onChange={(v) => set("name", v)}
      />
      <LocaleField
        label={t("admin.objects.description")}
        multiline
        value={form.description}
        locale={locale}
        error={errorAt(errors, "description")}
        onChange={(v) => set("description", v)}
      />

      <LocationPicker
        label={t("admin.objects.location")}
        lat={form.lat}
        lon={form.lon}
        fallbackCenter={spotLocation}
        contextPoint={spotLocation}
        objectType={form.type}
        {...(spot ? { threeD: { spot } } : {})}
        errors={{ lat: errors["location.lat"] ?? errors.location, lon: errors["location.lon"] }}
        onChange={(lat, lon) => {
          setForm((f) => ({ ...f, lat, lon }));
          setErrors((e) => ({ ...e, location: "", "location.lat": "", "location.lon": "" }));
        }}
      />

      {mode === "staff" ? (
        <>
          <Field
            label={t("admin.objects.override")}
            hint={t("admin.objects.overrideHint")}
            error={errorAt(errors, "depthOverrideM")}
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                className="adm-input ds-text-coordinate"
                inputMode="decimal"
                value={form.override}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                onChange={(e) => set("override", e.target.value)}
              />
            )}
          </Field>

          <Field label={t("admin.field.status")}>
            {() => (
              <SegmentedControl
                aria-label={t("admin.field.status")}
                value={form.status}
                onChange={(v) => set("status", v)}
                options={STATUSES.map((s) => ({ value: s, label: t(`admin.status.${s}`) }))}
              />
            )}
          </Field>
        </>
      ) : null}
    </>
  );
}
